import React, { useState, useMemo, useEffect } from "react"
import * as Icons from "lucide-react"

const STORAGE_KEY = "em-tasks"

function loadTasks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

const LISTS = [
  { key: "my-day", label: "My Day", icon: Icons.Sun },
  { key: "important", label: "Important", icon: Icons.Star },
  { key: "planned", label: "Planned", icon: Icons.CalendarClock },
  { key: "tasks", label: "Tasks", icon: Icons.CheckSquare },
]

export default function EmailTasks() {
  const [tasks, setTasks] = useState(loadTasks)
  const [activeList, setActiveList] = useState("tasks")
  const [input, setInput] = useState("")
  const [due, setDue] = useState("")

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks))
    } catch {}
  }, [tasks])

  const addTask = () => {
    const title = input.trim()
    if (!title) return
    setTasks(prev => [
      {
        id: `t-${Date.now()}`,
        title,
        done: false,
        important: activeList === "important",
        myDay: activeList === "my-day",
        due: due || null,
      },
      ...prev,
    ])
    setInput("")
    setDue("")
  }

  const toggleDone = id =>
    setTasks(prev => prev.map(t => (t.id === id ? { ...t, done: !t.done } : t)))
  const toggleImportant = id =>
    setTasks(prev =>
      prev.map(t => (t.id === id ? { ...t, important: !t.important } : t))
    )
  const removeTask = id => setTasks(prev => prev.filter(t => t.id !== id))

  const filtered = useMemo(() => {
    let list = tasks
    if (activeList === "important") list = tasks.filter(t => t.important)
    else if (activeList === "my-day") list = tasks.filter(t => t.myDay)
    else if (activeList === "planned") list = tasks.filter(t => t.due)
    const active = list.filter(t => !t.done)
    const done = list.filter(t => t.done)
    return { active, done }
  }, [tasks, activeList])

  const counts = useMemo(
    () => ({
      "my-day": tasks.filter(t => t.myDay && !t.done).length,
      important: tasks.filter(t => t.important && !t.done).length,
      planned: tasks.filter(t => t.due && !t.done).length,
      tasks: tasks.filter(t => !t.done).length,
    }),
    [tasks]
  )

  const activeMeta = LISTS.find(l => l.key === activeList)

  return (
    <div className="em-tasks">
      <div className="em-tasks-sidebar">
        <div className="em-sidebar-section-title" style={{ marginTop: 8 }}>
          Lists
        </div>
        {LISTS.map(l => {
          const IconComp = l.icon
          return (
            <div
              key={l.key}
              className={`em-sidebar-folder ${
                activeList === l.key ? "em-sidebar-folder-active" : ""
              }`}
              onClick={() => setActiveList(l.key)}
            >
              <span className="em-sidebar-folder-icon">
                <IconComp size={16} />
              </span>
              <span className="em-sidebar-folder-label">{l.label}</span>
              {counts[l.key] > 0 && (
                <span className="em-sidebar-folder-count">{counts[l.key]}</span>
              )}
            </div>
          )
        })}
      </div>

      <div className="em-tasks-main">
        <div className="em-tasks-header">
          {activeMeta && <activeMeta.icon size={22} />}
          <h2>{activeMeta?.label}</h2>
        </div>

        <div className="em-tasks-add">
          <Icons.Plus size={18} />
          <input
            placeholder="Add a task"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") addTask()
            }}
          />
          <input
            className="em-tasks-add-due"
            type="date"
            value={due}
            onChange={e => setDue(e.target.value)}
            title="Due date"
          />
          <button className="em-compose-send-btn" onClick={addTask}>
            Add
          </button>
        </div>

        <div className="em-tasks-scroll">
          {filtered.active.length === 0 && filtered.done.length === 0 && (
            <div className="em-tasks-empty">
              <Icons.CheckCircle2 size={44} strokeWidth={1} />
              <p>No tasks here</p>
              <span>Add a task above to get started</span>
            </div>
          )}

          {filtered.active.map(t => (
            <TaskRow
              key={t.id}
              task={t}
              onToggle={toggleDone}
              onImportant={toggleImportant}
              onRemove={removeTask}
            />
          ))}

          {filtered.done.length > 0 && (
            <div className="em-tasks-done-title">
              Completed ({filtered.done.length})
            </div>
          )}
          {filtered.done.map(t => (
            <TaskRow
              key={t.id}
              task={t}
              onToggle={toggleDone}
              onImportant={toggleImportant}
              onRemove={removeTask}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function TaskRow({ task, onToggle, onImportant, onRemove }) {
  return (
    <div className={`em-task-row ${task.done ? "em-task-row-done" : ""}`}>
      <button className="em-task-check" onClick={() => onToggle(task.id)}>
        {task.done ? (
          <Icons.CheckCircle2 size={18} />
        ) : (
          <Icons.Circle size={18} />
        )}
      </button>
      <div className="em-task-info">
        <div className="em-task-title">{task.title}</div>
        {task.due && (
          <div className="em-task-due">
            <Icons.CalendarClock size={12} /> {task.due}
          </div>
        )}
      </div>
      <button
        className={`em-task-star ${task.important ? "em-task-star-on" : ""}`}
        onClick={() => onImportant(task.id)}
      >
        <Icons.Star size={16} fill={task.important ? "#f59e0b" : "none"} />
      </button>
      <button className="em-task-delete" onClick={() => onRemove(task.id)}>
        <Icons.Trash2 size={15} />
      </button>
    </div>
  )
}
